import React, { ForwardedRef, forwardRef } from 'react';
import { Box, Typography, Tooltip, IconButton, alpha, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEasyBrowserContext } from '../browser';
import { MenuItemType } from './taskbartypes.d';
import iconMap from '../../../paginas/gestao/menu/iconmap';

/**
 * ItemTask - Representa uma aba/janela aberta na TaskBar com suporte a temas e ícones.
 */
const ItemTask = forwardRef(({ itemMenu }: { itemMenu: MenuItemType }, _ref: ForwardedRef<any>) => {
  const { fecharPagina, abrirPagina, taskSelecionada } = useEasyBrowserContext()!;
  const theme = useTheme();

  const isSelected = taskSelecionada?.id === itemMenu.id;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    fecharPagina(itemMenu);
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    abrirPagina(itemMenu);
  };

  return (
    <Tooltip title={itemMenu.descricao} arrow disableInteractive enterDelay={700}>
      <Box
        ref={_ref}
        onClick={handleSelect}
        sx={{
          display: 'flex',
          height: '34px', // Um pouco mais alto para conforto no toque
          width: '160px',
          minWidth: '160px',
          overflow: 'hidden',
          alignItems: 'center',
          position: 'relative',
          borderRadius: '6px 6px 0 0', // Formato de aba de navegador
          px: 1.5,
          mx: 0.2,
          transition: 'all 0.15s ease-in-out',
          cursor: 'pointer',
          
          // Bordas e Fundo baseados no Tema
          border: '1px solid',
          borderColor: isSelected ? theme.palette.divider : 'transparent',
          borderBottom: 'none',
          bgcolor: isSelected 
            ? theme.palette.background.default 
            : alpha(theme.palette.action.active, 0.03),

          // Sombra sutil para profundidade na aba ativa
          boxShadow: isSelected ? '0 -2px 8px rgba(0,0,0,0.05)' : 'none',

          // Indicador de cor primária na base (estilo VS Code/Chrome)
          "&::after": {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: isSelected ? '2px' : '0px',
            bgcolor: theme.palette.primary.main,
            transition: 'height 0.2s',
          },

          "&:hover": {
            bgcolor: isSelected 
                ? theme.palette.background.default 
                : alpha(theme.palette.primary.main, 0.08),
            "& .close-button": {
              opacity: 0.8
            }
          },
        }}
      >
        {/* Ícone da Tela */}
        <Box sx={{ 
            display: 'flex', 
            mr: 1, 
            color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
            '& svg': { fontSize: 16 } 
        }}>
            {itemMenu.icone ? iconMap[itemMenu.icone] : iconMap["default"]}
        </Box>

        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
            userSelect: 'none',
            flexGrow: 1,
            fontSize: '12px',
            fontWeight: isSelected ? 700 : 500,
            pr: 2 
          }}
        >
          {itemMenu.descricao}
        </Typography>

        {/* Botão Fechar otimizado */}
        <Box
          className="close-button"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '4px',
            color: isSelected ? 'inherit' : 'text.disabled',
            opacity: isSelected ? 0.6 : 0,
            transition: 'all 0.2s',
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.15),
              color: theme.palette.error.main,
              opacity: '1 !important',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '14px' }} />
        </Box>
      </Box>
    </Tooltip>
  );
});

ItemTask.displayName = 'ItemTask';

export default ItemTask;